import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MedalIcon, UsersIcon, ShieldIcon, MailIcon, MapPinIcon, CalendarDaysIcon, ClockIcon, CheckCircleIcon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/SectionHeader';
import { ValuesSection } from '../components/ValuesSection';
export function BadmintonPage() {
  return <main className="bg-white pt-20" data-id="element-384">
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px] overflow-hidden bg-brand-blue" data-id="element-385">
        <img src="https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-386" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-387"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-388">
          <div className="mb-8" data-id="element-389">
            <nav aria-label="Breadcrumb" className="flex text-sm font-medium text-white/80 mb-8" data-id="element-390">
              <Link to="/" className="hover:text-white transition-colors" data-id="element-391">
                Home
              </Link>
              <span className="mx-2" data-id="element-392">/</span>
              <span className="cursor-default" data-id="element-393">Activities</span>
              <span className="mx-2" data-id="element-394">/</span>
              <span className="text-white" data-id="element-395">Badminton</span>
            </nav>
          </div>

          <div className="max-w-3xl" data-id="element-396">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-397">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight" data-id="element-398">
                From First Racket to Competition Ready
              </h1>
              <p className="text-xl text-white/90 mb-10 max-w-2xl leading-relaxed" data-id="element-399">
                Olympian-led badminton coaching developing technique, agility,
                and game sense for young athletes of all abilities.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-400">
                <Button variant="primary" size="lg" href="/book" data-id="element-401">
                  Book Now
                </Button>
                <Button variant="secondary" size="lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
                behavior: 'smooth'
              })} className="border-white text-white hover:bg-white/20" data-id="element-402">
                  Contact Us
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-403">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-404">
          <div data-id="element-405">
            <SectionHeader eyebrow="OVERVIEW" title="About Our Badminton Sessions" align="left" data-id="element-406" />
            <div className="space-y-6 text-lg text-navy-600" data-id="element-407">
              <p className="leading-relaxed" data-id="element-408">
                Our badminton programme is led by our team of Olympian coaches —
                Donna Kellogg MBE (Triple Olympian, 2000, 2004 & 2008), Natalie
                Barton (2004 Olympian, world top 5) and Sarah Chapman (2000
                Olympian, European medallist). Together they bring decades of
                elite experience to every session.
              </p>
              <p className="leading-relaxed" data-id="element-409">
                Whether your child is picking up a racket for the first time or
                looking to compete at county level, our structured sessions
                develop technique, agility, and game sense in a fun, supportive
                environment. We focus on personal improvement and celebrate
                small victories every week.
              </p>
              <p className="leading-relaxed" data-id="element-410">
                All necessary equipment, including age-appropriate rackets and
                shuttles, is provided. Children just need comfortable sports
                clothing and non-marking indoor trainers.
              </p>
            </div>
          </div>

          <div className="bg-navy-50 rounded-2xl p-8 md:p-12 border border-navy-100 relative overflow-hidden" data-id="element-411">
            <div className="absolute top-0 left-0 w-2 h-full bg-brand-blue" data-id="element-412"></div>
            <h3 className="text-2xl font-bold text-navy-900 mb-8" data-id="element-413">
              Programme Highlights
            </h3>
            <ul className="space-y-6" data-id="element-414">
              <li className="flex items-start" data-id="element-415">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-gold-500" data-id="element-416">
                  <MedalIcon className="w-5 h-5" data-id="element-417" />
                </div>
                <div data-id="element-418">
                  <h4 className="font-bold text-navy-900" data-id="element-419">
                    Led by Our Olympian Coaches
                  </h4>
                  <p className="text-navy-600 text-sm mt-1" data-id="element-420">
                    Donna Kellogg MBE, Natalie Barton and Sarah Chapman bring
                    elite experience to every session.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-421">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-gold-500" data-id="element-422">
                  <UsersIcon className="w-5 h-5" data-id="element-423" />
                </div>
                <div data-id="element-424">
                  <h4 className="font-bold text-navy-900" data-id="element-425">Small Group Sizes</h4>
                  <p className="text-navy-600 text-sm mt-1" data-id="element-426">
                    High coach-to-player ratio ensures individual attention for
                    every child.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-427">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-gold-500" data-id="element-428">
                  <ShieldIcon className="w-5 h-5" data-id="element-429" />
                </div>
                <div data-id="element-430">
                  <h4 className="font-bold text-navy-900" data-id="element-431">Safe & Inclusive</h4>
                  <p className="text-navy-600 text-sm mt-1" data-id="element-432">
                    A welcoming environment for all abilities and backgrounds.
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Badminton in Schools */}
      <section className="py-20 md:py-24 bg-navy-50 border-t border-gray-100" data-id="element-433">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-434">
          <div className="mb-12" data-id="element-435">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase mb-3" data-id="element-436">
              Badminton in Schools
            </h2>
            <h3 className="text-xl font-bold text-brand-blue uppercase tracking-wide" data-id="element-437">
              Inspire &bull; Encourage &bull; Achieve
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-438">
            <div className="lg:col-span-3 space-y-6 text-lg text-navy-700 leading-relaxed" data-id="element-439">
              <p data-id="element-440">
                Bright Stars Sports coaches currently support a number of
                primary and senior schools across Derbyshire, and we are very
                interested in growing our school partnerships to bring the
                benefits of badminton to more students.
              </p>
              <p data-id="element-441">
                We have extensive experience delivering engaging sessions in a
                variety of spaces, from full sports halls to smaller gymnasiums.
                To make it as easy as possible for schools, we provide all the
                necessary equipment required for delivery, including
                age-appropriate rackets and shuttles.
              </p>

              <div className="mt-8 p-6 bg-white rounded-xl border border-navy-100 shadow-sm inline-block" data-id="element-442">
                <h4 className="font-bold text-navy-900 mb-2 flex items-center" data-id="element-443">
                  <MailIcon className="w-5 h-5 text-brand-blue mr-2" data-id="element-444" />
                  Get in Touch
                </h4>
                <p className="text-base text-navy-600 mb-3" data-id="element-445">
                  To discuss introducing badminton to your students, please
                  email:
                </p>
                <a href="mailto:derby@brightstarsports.com" className="text-brand-blue hover:text-brand-red font-bold text-lg transition-colors" data-id="element-446">
                  derby@brightstarsports.com
                </a>
              </div>
            </div>

            <div className="lg:col-span-2" data-id="element-447">
              <div className="bg-white rounded-2xl p-8 border border-navy-100 shadow-sm h-full" data-id="element-448">
                <h4 className="text-xl font-bold text-navy-900 mb-6 border-b border-navy-50 pb-4" data-id="element-449">
                  What We Offer Schools
                </h4>
                <ul className="space-y-5" data-id="element-450">
                  {['School festivals and sports days', 'Open days and taster sessions', 'Weekly breakfast clubs', 'After-school badminton clubs', 'Curriculum PE support', 'All equipment provided'].map((item, i) => <li key={i} className="flex items-start" data-id="element-451">
                      <CheckCircleIcon className="w-6 h-6 text-brand-green mr-3 flex-shrink-0" data-id="element-452" />
                      <span className="text-navy-700 font-medium" data-id="element-453">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Badminton Classes */}
      <section className="py-20 md:py-24 bg-white border-t border-gray-100" data-id="element-454">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-455">
          <div className="mb-12" data-id="element-456">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase mb-3" data-id="element-457">
              Badminton Classes
            </h2>
            <h3 className="text-xl font-bold text-brand-blue uppercase tracking-wide" data-id="element-458">
              Age Groups 7 - 18 Years
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-459">
            <div className="lg:col-span-3 space-y-6 text-lg text-navy-700 leading-relaxed" data-id="element-460">
              <p data-id="element-461">
                Our age-appropriate badminton classes encourage budding players
                to learn new skills, techniques, and tactics. Players are
                introduced to grip techniques, the essential shots needed for
                the game, scoring, and both fun and competitive gameplay.
              </p>
              <p data-id="element-462">
                Beyond the physical skills, our classes provide players with
                opportunities to develop their discipline, motivation, and
                confidence through structured coaching and progressive
                challenges. Warm-ups and games are great for building teamwork,
                learning to cooperate, meeting new friends, and having fun in a
                supportive environment.
              </p>
              <p className="text-base text-navy-500 italic mt-4" data-id="element-463">
                * The age range is a guideline. If younger children have played
                other racket sports before or are capable of striking the
                shuttle, please get in touch.
              </p>
            </div>

            <div className="lg:col-span-2" data-id="element-464">
              <div className="bg-navy-50 rounded-2xl p-8 border border-navy-100 h-full" data-id="element-465">
                <h4 className="text-xl font-bold text-navy-900 mb-6 border-b border-navy-100 pb-4" data-id="element-466">
                  Class Focus Areas
                </h4>
                <ul className="space-y-5" data-id="element-467">
                  {['Grip technique and shot execution', 'Court movement and agility', 'Tactical awareness and scoring', 'Discipline and self-motivation', 'Teamwork and cooperation', 'Confidence building'].map((item, i) => <li key={i} className="flex items-start" data-id="element-468">
                      <CheckCircleIcon className="w-6 h-6 text-brand-blue mr-3 flex-shrink-0" data-id="element-469" />
                      <span className="text-navy-700 font-medium" data-id="element-470">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ladies Classes */}
      <section className="py-20 md:py-24 bg-navy-50 border-t border-gray-100" data-id="element-471">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-472">
          <div className="mb-12" data-id="element-473">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase" data-id="element-474">
              Ladies Classes
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 mb-12" data-id="element-475">
            <div className="lg:col-span-3 space-y-6 text-lg text-navy-700 leading-relaxed" data-id="element-476">
              <p data-id="element-477">
                Our Ladies Classes are aimed at social and beginner players
                wanting to learn new skills, keep active, and socialise with
                other like-minded ladies on a Thursday morning.
              </p>
              <p data-id="element-478">
                It is a very friendly group with a wide range of ages, and
                everyone is made to feel incredibly welcome. The sessions are
                always good fun and guarantee weekly laughs with a lovely group
                of ladies. If you are thinking about it, come and give it a go!
              </p>
              <p data-id="element-479">
                We operate on a Pay As You Go basis, providing the flexibility
                you might require to balance work and other areas of your life.
                Plus, we always stay on afterwards for a debrief and a cup of
                tea for those who have the time.
              </p>
            </div>

            <div className="lg:col-span-2" data-id="element-480">
              <div className="bg-white rounded-2xl p-8 border border-navy-100 shadow-sm h-full" data-id="element-481">
                <h4 className="text-xl font-bold text-navy-900 mb-6 border-b border-navy-50 pb-4" data-id="element-482">
                  Session Highlights
                </h4>
                <ul className="space-y-5" data-id="element-483">
                  {['Social and beginner friendly', 'Welcoming community of all ages', 'Focus on fun and staying active', 'Flexible Pay As You Go booking', 'Post-session tea and chat'].map((item, i) => <li key={i} className="flex items-start" data-id="element-484">
                      <CheckCircleIcon className="w-6 h-6 text-brand-coral mr-3 flex-shrink-0" data-id="element-485" />
                      <span className="text-navy-700 font-medium" data-id="element-486">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-brand-blue p-8 md:p-10 text-center max-w-4xl mx-auto shadow-lg" data-id="element-487">
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-488">
              <MapPinIcon className="w-6 h-6 text-brand-yellow" data-id="element-489" />
              <span className="text-sm font-bold tracking-widest uppercase text-white/80" data-id="element-490">
                Session Location
              </span>
            </div>
            <h4 className="text-2xl md:text-3xl font-display font-bold text-white mb-6" data-id="element-491">
              Springwood Leisure Centre, Oakwood, Derby
            </h4>
            <div className="flex flex-wrap justify-center gap-6 md:gap-12" data-id="element-492">
              <div className="flex items-center gap-3 text-white font-medium text-lg bg-white/10 px-6 py-3 rounded-full" data-id="element-493">
                <CalendarDaysIcon className="w-6 h-6 text-brand-yellow" data-id="element-494" />
                <span data-id="element-495">Thursdays</span>
              </div>
              <div className="flex items-center gap-3 text-white font-medium text-lg bg-white/10 px-6 py-3 rounded-full" data-id="element-496">
                <ClockIcon className="w-6 h-6 text-brand-yellow" data-id="element-497" />
                <span data-id="element-498">10:00 - 11:00 am</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pay and Play */}
      <section className="py-20 md:py-24 bg-white border-t border-gray-100" data-id="element-499">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-500">
          <div className="mb-12" data-id="element-501">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase mb-3" data-id="element-502">
              Pay and Play Badminton
            </h2>
            <h3 className="text-xl font-bold text-brand-blue uppercase tracking-wide" data-id="element-503">
              Adults Weekly Social Sessions
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 mb-12" data-id="element-504">
            <div className="lg:col-span-3 space-y-6 text-lg text-navy-700 leading-relaxed" data-id="element-505">
              <p data-id="element-506">
                For players of all abilities that just want to PLAY! This
                session is an opportunity for players to come together and enjoy
                a fun and social game of badminton.
              </p>
              <p data-id="element-507">
                It's a great way to get active, meet new people, and have fun
                without the commitment of joining a club or finding your own
                court. You can come along as an individual or bring a friend.
              </p>
              <p data-id="element-508">
                This session also serves as an additional opportunity for junior
                players (17+ years) who have been through the Bright Star Sports
                weekly classes and are looking to continue gameplay to improve
                their tactical knowledge and build confidence against adult
                players.
              </p>

              <div className="pt-4" data-id="element-509">
                <Button href="https://www.brightstarsports.com" variant="primary" className="inline-flex items-center" data-id="element-510">
                  Book Your Session
                  <ExternalLinkIcon className="w-4 h-4 ml-2" data-id="element-511" />
                </Button>
                <p className="text-sm font-bold text-brand-blue mt-3" data-id="element-512">
                  We can't wait to welcome you to the courts!
                </p>
              </div>
            </div>

            <div className="lg:col-span-2" data-id="element-513">
              <div className="bg-navy-50 rounded-2xl p-8 border border-navy-100 h-full" data-id="element-514">
                <h4 className="text-xl font-bold text-navy-900 mb-6 border-b border-navy-100 pb-4" data-id="element-515">
                  Why Join Pay & Play?
                </h4>
                <ul className="space-y-5" data-id="element-516">
                  {['Open to all abilities', 'No club commitment required', 'Come alone or with friends', 'Fun, social game play', 'Junior pathway (17+ years)'].map((item, i) => <li key={i} className="flex items-start" data-id="element-517">
                      <CheckCircleIcon className="w-6 h-6 text-brand-red mr-3 flex-shrink-0" data-id="element-518" />
                      <span className="text-navy-700 font-medium" data-id="element-519">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-brand-blue p-8 md:p-10 text-center max-w-4xl mx-auto shadow-lg" data-id="element-520">
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-521">
              <MapPinIcon className="w-6 h-6 text-brand-yellow" data-id="element-522" />
              <span className="text-sm font-bold tracking-widest uppercase text-white/80" data-id="element-523">
                Session Location
              </span>
            </div>
            <h4 className="text-2xl md:text-3xl font-display font-bold text-white mb-6" data-id="element-524">
              Lees Brook Academy Chaddesden, Derby, DE21 4QX
            </h4>
            <div className="flex flex-wrap justify-center gap-6 md:gap-12" data-id="element-525">
              <div className="flex items-center gap-3 text-white font-medium text-lg bg-white/10 px-6 py-3 rounded-full" data-id="element-526">
                <CalendarDaysIcon className="w-6 h-6 text-brand-yellow" data-id="element-527" />
                <span data-id="element-528">Mondays</span>
              </div>
              <div className="flex items-center gap-3 text-white font-medium text-lg bg-white/10 px-6 py-3 rounded-full" data-id="element-529">
                <ClockIcon className="w-6 h-6 text-brand-yellow" data-id="element-530" />
                <span data-id="element-531">7:30 - 9:00 pm</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Olympic Values */}
      <ValuesSection ctaLabel="Book a Session" ctaHref="/book" data-id="element-532" />
    </main>;
}